import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { 
 Card, CardHeader, CardTitle, CardContent, CardDescription,
 Button, Input, Select, Modal, ModalFooter, Badge, Alert, AlertDescription
} from '../components/ui';
import { formatDate, formatNumber } from '../lib/utils';
import { 
 Plus, Trash2, RefreshCw, FileText, Eye, Edit, Copy, ExternalLink,
 Globe, Layout, Loader2, BarChart2, Settings, Save, Image, Type,
 Palette, Code, Link as LinkIcon
} from 'lucide-react';
import toast from 'react-hot-toast';

// Landing page templates
const TEMPLATES = [
 { id: 'default', name: 'Minimal', description: 'Clean, simple landing page', preview: '🎯' },
 { id: 'lead-capture', name: 'Lead Capture', description: 'Form-focused with email signup', preview: '📧' },
 { id: 'product', name: 'Product Launch', description: 'Feature highlights & CTA', preview: '🚀' },
 { id: 'coming-soon', name: 'Coming Soon', description: 'Countdown with teaser', preview: '⏰' },
 { id: 'link-bio', name: 'Link in Bio', description: 'Social media links page', preview: '🔗' },
];

export default function LandingPages() {
 const { user, profile, isPro, isAdmin } = useAuth();
 const [pages, setPages] = useState([]);
 const [domains, setDomains] = useState([]);
 const [defaultDomain, setDefaultDomain] = useState(null);
 const [loading, setLoading] = useState(true);
 const [showAddModal, setShowAddModal] = useState(false);
 const [showEditModal, setShowEditModal] = useState(false);
 const [showEditorModal, setShowEditorModal] = useState(false);
 const [selectedPage, setSelectedPage] = useState(null);
 const [saving, setSaving] = useState(false);
 const [activeTab, setActiveTab] = useState('content');

 // Form state
 const [formData, setFormData] = useState({
 title: '',
 slug: '',
 template: 'default',
 customDomainId: '',
 metaTitle: '',
 metaDescription: '',
 ogImage: '',
 status: 'draft',
 themeColor: '#10b981',
 content: {
 headline: 'Welcome to Our Page',
 subheadline: 'Add your compelling message here',
 cta_text: 'Get Started',
 cta_url: '#',
 body_text: '',
 image_url: '',
 show_logo: true,
 links: [],
 },
 trackingCodes: {
 google_analytics: '',
 facebook_pixel: '',
 custom_head: '',
 },
 });

 const canUseCustomDomains = isPro || isAdmin || profile?.role === 'superadmin';

 const loadData = useCallback(async () => {
 if (!user) return;
 setLoading(true);
 
 try {
 // Load landing pages
 const { data: pagesData, error: pagesError } = await supabase
 .from('landing_pages')
 .select('*, custom_domains(domain, status)')
 .eq('user_id', user.id)
 .order('created_at', { ascending: false });

 if (pagesError) throw pagesError;
 setPages(pagesData || []);

 // Load user's verified custom domains (if Pro)
 if (canUseCustomDomains) {
 const { data: domainsData } = await supabase
 .from('custom_domains')
 .select('*')
 .eq('user_id', user.id)
 .eq('status', 'verified');
 
 setDomains(domainsData || []);
 }

 // Load default domain
 const { data: settings } = await supabase
 .from('admin_settings')
 .select('value')
 .eq('key', 'default_landing_domain')
 .maybeSingle();

 if (settings?.value) {
 const parsed = typeof settings.value === 'string' ? JSON.parse(settings.value) : settings.value;
 setDefaultDomain(parsed);
 }

 } catch (error) {
 console.error('Error:', error);
 toast.error('Failed to load data');
 } finally {
 setLoading(false);
 }
 }, [user, canUseCustomDomains]);

 useEffect(() => { loadData(); }, [loadData]);

 const resetForm = () => {
 setFormData({
 title: '',
 slug: '',
 template: 'default',
 customDomainId: '',
 metaTitle: '',
 metaDescription: '',
 ogImage: '',
 status: 'draft',
 themeColor: '#10b981',
 content: {
 headline: 'Welcome to Our Page',
 subheadline: 'Add your compelling message here',
 cta_text: 'Get Started',
 cta_url: '#',
 body_text: '',
 image_url: '',
 show_logo: true,
 links: [],
 },
 trackingCodes: {
 google_analytics: '',
 facebook_pixel: '',
 custom_head: '',
 },
 });
 setActiveTab('content');
 };

 const generateSlug = (title) => {
 return title
 .toLowerCase()
 .replace(/[^a-z0-9]+/g, '-')
 .replace(/^-|-$/g, '')
 .substring(0, 50);
 };

 const handleTitleChange = (e) => {
 const title = e.target.value;
 setFormData(prev => ({
 ...prev,
 title,
 slug: prev.slug || generateSlug(title),
 }));
 };

 const updateContent = (key, value) => {
 setFormData(prev => ({
 ...prev,
 content: { ...prev.content, [key]: value }
 }));
 };

 const updateTracking = (key, value) => {
 setFormData(prev => ({
 ...prev,
 trackingCodes: { ...prev.trackingCodes, [key]: value }
 }));
 };

 const handleAddPage = async (e) => {
 e.preventDefault();
 
 if (!formData.title.trim() || !formData.slug.trim()) {
 toast.error('Title and URL slug are required');
 return;
 }

 const slugRegex = /^[a-z0-9-]+$/;
 if (!slugRegex.test(formData.slug)) {
 toast.error('URL slug can only contain lowercase letters, numbers, and hyphens');
 return;
 }

 setSaving(true);
 
 try {
 const { error } = await supabase
 .from('landing_pages')
 .insert({
 user_id: user.id,
 title: formData.title.trim(),
 slug: formData.slug.trim(),
 template: formData.template,
 custom_domain_id: formData.customDomainId || null,
 meta_title: formData.metaTitle || formData.title,
 meta_description: formData.metaDescription || '',
 og_image: formData.ogImage || null,
 theme_color: formData.themeColor,
 status: formData.status,
 content: formData.content,
 tracking_codes: formData.trackingCodes,
 });

 if (error) {
 if (error.code === '23505') {
 toast.error('A page with this URL slug already exists');
 } else {
 throw error;
 }
 setSaving(false);
 return;
 }

 // Success - close modal first, then refresh
 setSaving(false);
 setShowAddModal(false);
 resetForm();
 toast.success('Landing page created!');
 await loadData();
 } catch (error) {
 console.error('Error:', error);
 toast.error(error.message || 'Failed to create page');
 setSaving(false);
 }
 };

 const handleSavePage = async () => {
 if (!selectedPage) return;

 setSaving(true);
 
 try {
 // When custom domain: write user slug to custom_slug, never overwrite default slug
 // When default domain: slug is read-only (random), don't update it
 const slugUpdate = formData.customDomainId
   ? { custom_slug: formData.slug.trim() }
   : {}; // default slug is immutable from this form

 const { error } = await supabase
 .from('landing_pages')
 .update({
 title: formData.title.trim(),
 ...slugUpdate,
 template: formData.template,
 custom_domain_id: formData.customDomainId || null,
 meta_title: formData.metaTitle || formData.title,
 meta_description: formData.metaDescription || '',
 og_image: formData.ogImage || null,
 theme_color: formData.themeColor,
 status: formData.status,
 content: formData.content,
 tracking_codes: formData.trackingCodes,
 updated_at: new Date().toISOString(),
 published_at: formData.status === 'published' && !selectedPage.published_at 
 ? new Date().toISOString() 
 : selectedPage.published_at,
 })
 .eq('id', selectedPage.id);

 if (error) throw error;

 toast.success('Page saved!');
 loadData();
 } catch (error) {
 console.error('Error:', error);
 toast.error(error.message || 'Failed to save page');
 } finally {
 setSaving(false);
 }
 };

 const handleDeletePage = async (page) => {
 if (!window.confirm(`Delete "${page.title}"? This cannot be undone.`)) return;

 try {
 const { error } = await supabase
 .from('landing_pages')
 .delete()
 .eq('id', page.id);

 if (error) throw error;

 toast.success('Page deleted');
 loadData();
 } catch (error) {
 toast.error('Failed to delete page');
 }
 };

 const openEditor = (page) => {
 setSelectedPage(page);
 setFormData({
 title: page.title || '',
 // When custom domain: show custom_slug (user-set) in the slug field
 // When default domain: show default_slug (random) — read-only
 slug: page.custom_domain_id ? (page.custom_slug || '') : (page.default_slug || page.slug || ''),
 template: page.template || 'default',
 customDomainId: page.custom_domain_id || '',
 metaTitle: page.meta_title || '',
 metaDescription: page.meta_description || '',
 ogImage: page.og_image || '',
 status: page.status || 'draft',
 themeColor: page.theme_color || '#10b981',
 content: page.content || {},
 trackingCodes: page.tracking_codes || {},
 });
 setActiveTab('content');
 setShowEditorModal(true);
 };

 const getPageUrl = (page) => {
 if (page.custom_domain_id && page.custom_domains?.domain) {
   // Custom domain: use custom_slug (user-set), fall back to slug
   const path = page.custom_slug || page.slug;
   return `https://${page.custom_domains.domain}/${path}`;
 }
 // Default domain: use default_slug (random, permanent), fall back to slug
 const domain = defaultDomain?.domain || 'pages.revhapi.com';
 const path = page.default_slug || page.slug;
 return `https://${domain}/${path}`;
 };

 const copyPageUrl = (page) => {
 navigator.clipboard.writeText(getPageUrl(page));
 toast.success('URL copied to clipboard');
 };

 return (
 <div className="space-y-6">
 {/* Header */}
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
 <div>
 <h1 className="text-2xl font-bold">Landing Pages</h1>
 <p className="text-muted-foreground">Create and manage your landing pages</p>
 </div>
 <div className="flex gap-2">
 <Button variant="outline" onClick={loadData} disabled={loading}>
 <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
 Refresh
 </Button>
 <Button onClick={() => { resetForm(); setShowAddModal(true); }}>
 <Plus className="h-4 w-4 mr-2" />
 Create Page
 </Button>
 </div>
 </div>

 {/* Domain Info */}
 <Alert>
 <Globe className="h-4 w-4" />
 <AlertDescription>
 {canUseCustomDomains && domains.length > 0 ? (
 <>
 You have <strong>{domains.length}</strong> verified custom domain(s).
 <span className="text-muted-foreground ml-1">
 Default: {defaultDomain?.domain || 'pages.revhapi.com'}
 </span>
 </>
 ) : (
 <>
 Your pages will be published on <strong>{defaultDomain?.domain || 'pages.revhapi.com'}</strong>
 {!canUseCustomDomains && (
 <span className="ml-1">
 • <a href="/custom-domains" className="text-primary underline">Upgrade to Pro</a> for custom domains
 </span>
 )}
 </>
 )}
 </AlertDescription>
 </Alert>

 {/* Pages Grid */}
 <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
 {loading ? (
 <div className="col-span-full flex justify-center py-12">
 <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
 </div>
 ) : pages.length === 0 ? (
 <Card className="col-span-full">
 <CardContent className="flex flex-col items-center justify-center py-12">
 <FileText className="h-12 w-12 text-muted-foreground mb-4" />
 <p className="text-muted-foreground mb-4">No landing pages yet</p>
 <Button onClick={() => { resetForm(); setShowAddModal(true); }}>
 <Plus className="h-4 w-4 mr-2" />
 Create Your First Page
 </Button>
 </CardContent>
 </Card>
 ) : (
 pages.map(page => (
 <Card key={page.id} className="hover:shadow-md transition-shadow overflow-hidden">
 {/* Preview header */}
 <div 
 className="h-20 flex items-center justify-center text-4xl"
 style={{ backgroundColor: page.theme_color + '20' }}
 >
 {TEMPLATES.find(t => t.id === page.template)?.preview || '📄'}
 </div>
 
 <CardContent className="pt-4">
 <div className="flex items-start justify-between mb-2">
 <Badge variant={page.status === 'published' ? 'success' : 'secondary'}>
 {page.status}
 </Badge>
 <div className="flex gap-1">
 <Button size="icon" variant="ghost" onClick={() => openEditor(page)}>
 <Edit className="h-4 w-4" />
 </Button>
 <Button size="icon" variant="ghost" onClick={() => handleDeletePage(page)}>
 <Trash2 className="h-4 w-4 text-red-500" />
 </Button>
 </div>
 </div>

 <h3 className="font-semibold mb-1 truncate">{page.title}</h3>
 
 <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
 <Globe className="h-3 w-3" />
 <span className="truncate text-xs">
   {page.custom_domain_id && page.custom_domains?.domain
     ? `${page.custom_domains.domain}/${page.custom_slug || page.slug}`
     : `${defaultDomain?.domain || 'pages.revhapi.com'}/${page.default_slug || page.slug}`
   }
 </span>
 </div>

 <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
 <span>{TEMPLATES.find(t => t.id === page.template)?.name || 'Custom'}</span>
 <span className="flex items-center gap-1">
 <Eye className="h-3 w-3" />
 {formatNumber(page.views || 0)} views
 </span>
 </div>

 <div className="flex gap-2">
 <Button size="sm" variant="outline" className="flex-1" onClick={() => copyPageUrl(page)}>
 <Copy className="h-3 w-3 mr-1" />
 Copy URL
 </Button>
 {page.status === 'published' && (
 <Button size="sm" variant="outline" onClick={() => window.open(getPageUrl(page), '_blank')}>
 <ExternalLink className="h-3 w-3" />
 </Button>
 )}
 </div>
 </CardContent>
 </Card>
 ))
 )}
 </div>

 {/* Add Page Modal */}
 <Modal
 isOpen={showAddModal}
 onClose={() => { setShowAddModal(false); resetForm(); }}
 title="Create Landing Page"
 className="max-w-lg"
 >
 <form onSubmit={handleAddPage}>
 <div className="space-y-4">
 <Input
 label="Page Title *"
 placeholder="My Awesome Landing Page"
 value={formData.title}
 onChange={handleTitleChange}
 required
 />

 <Input
 label="URL Slug *"
 placeholder="my-page"
 value={formData.slug}
 onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
 hint={formData.customDomainId
   ? `${domains.find(d => d.id === formData.customDomainId)?.domain || 'customdomain.com'}/${formData.slug || 'your-slug'}`
   : `${defaultDomain?.domain || 'pages.revhapi.com'}/${formData.slug || 'your-slug'}`
 }
 required
 />

 <div>
 <label className="text-sm font-medium">Template</label>
 <div className="grid grid-cols-2 gap-2 mt-2">
 {TEMPLATES.map(t => (
 <button
 key={t.id}
 type="button"
 onClick={() => setFormData({ ...formData, template: t.id })}
 className={`p-3 border rounded-lg text-left transition-colors ${
 formData.template === t.id 
 ? 'border-primary bg-primary/5' 
 : 'hover:bg-muted/40'
 }`}
 >
 <span className="text-2xl">{t.preview}</span>
 <p className="font-medium text-sm mt-1">{t.name}</p>
 <p className="text-xs text-muted-foreground">{t.description}</p>
 </button>
 ))}
 </div>
 </div>

 {canUseCustomDomains && domains.length > 0 && (
 <div>
 <label className="text-sm font-medium">Domain</label>
 <Select
 value={formData.customDomainId}
 onChange={(e) => setFormData({ ...formData, customDomainId: e.target.value })}
 className="mt-1"
 >
 <option value="">Default ({defaultDomain?.domain || 'pages.revhapi.com'})</option>
 {domains.map(d => (
 <option key={d.id} value={d.id}>{d.domain}</option>
 ))}
 </Select>
 </div>
 )}
 </div>

 <ModalFooter>
 <Button variant="outline" type="button" onClick={() => { setShowAddModal(false); resetForm(); }}>
 Cancel
 </Button>
 <Button type="submit" loading={saving}>
 Create Page
 </Button>
 </ModalFooter>
 </form>
 </Modal>

 {/* Page Editor Modal */}
 <Modal
 isOpen={showEditorModal}
 onClose={() => { setShowEditorModal(false); setSelectedPage(null); }}
 title={`Edit: ${formData.title}`}
 className="max-w-4xl max-h-[90vh]"
 >
 <div className="flex flex-col h-full">
 {/* Tabs */}
 <div className="flex border-b mb-4">
 {[
 { id: 'content', label: 'Content', icon: Type },
 { id: 'design', label: 'Design', icon: Palette },
 { id: 'seo', label: 'SEO', icon: Globe },
 { id: 'tracking', label: 'Tracking', icon: Code },
 { id: 'settings', label: 'Settings', icon: Settings },
 ].map(tab => (
 <button
 key={tab.id}
 onClick={() => setActiveTab(tab.id)}
 className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
 activeTab === tab.id 
 ? 'border-primary text-primary' 
 : 'border-transparent text-muted-foreground hover:text-foreground'
 }`}
 >
 <tab.icon className="h-4 w-4" />
 {tab.label}
 </button>
 ))}
 </div>

 {/* Tab Content */}
 <div className="flex-1 overflow-y-auto space-y-4 pr-2">
 {activeTab === 'content' && (
 <>
 <Input
 label="Headline"
 placeholder="Your main headline"
 value={formData.content.headline || ''}
 onChange={(e) => updateContent('headline', e.target.value)}
 />
 <Input
 label="Subheadline"
 placeholder="Supporting text"
 value={formData.content.subheadline || ''}
 onChange={(e) => updateContent('subheadline', e.target.value)}
 />
 <div className="grid grid-cols-2 gap-4">
 <Input
 label="CTA Button Text"
 placeholder="Get Started"
 value={formData.content.cta_text || ''}
 onChange={(e) => updateContent('cta_text', e.target.value)}
 />
 <Input
 label="CTA Button URL"
 placeholder="https://..."
 value={formData.content.cta_url || ''}
 onChange={(e) => updateContent('cta_url', e.target.value)}
 />
 </div>
 <div>
 <label className="text-sm font-medium">Body Text</label>
 <textarea
 className="w-full mt-1 p-3 border rounded-lg min-h-[120px]"
 placeholder="Additional content..."
 value={formData.content.body_text || ''}
 onChange={(e) => updateContent('body_text', e.target.value)}
 />
 </div>
 <Input
 label="Hero Image URL"
 placeholder="https://..."
 value={formData.content.image_url || ''}
 onChange={(e) => updateContent('image_url', e.target.value)}
 />
 </>
 )}

 {activeTab === 'design' && (
 <>
 <div>
 <label className="text-sm font-medium">Theme Color</label>
 <div className="flex items-center gap-3 mt-2">
 <input
 type="color"
 value={formData.themeColor}
 onChange={(e) => setFormData({ ...formData, themeColor: e.target.value })}
 className="w-12 h-12 rounded cursor-pointer"
 />
 <Input
 value={formData.themeColor}
 onChange={(e) => setFormData({ ...formData, themeColor: e.target.value })}
 className="flex-1"
 />
 </div>
 </div>
 <div>
 <label className="text-sm font-medium">Template</label>
 <Select
 value={formData.template}
 onChange={(e) => setFormData({ ...formData, template: e.target.value })}
 className="mt-1"
 >
 {TEMPLATES.map(t => (
 <option key={t.id} value={t.id}>{t.preview} {t.name}</option>
 ))}
 </Select>
 </div>
 </>
 )}

 {activeTab === 'seo' && (
 <>
 <Input
 label="Meta Title"
 placeholder="SEO title (defaults to page title)"
 value={formData.metaTitle}
 onChange={(e) => setFormData({ ...formData, metaTitle: e.target.value })}
 hint="Recommended: 50-60 characters"
 />
 <div>
 <label className="text-sm font-medium">Meta Description</label>
 <textarea
 className="w-full mt-1 p-3 border rounded-lg min-h-[80px]"
 placeholder="SEO description"
 value={formData.metaDescription}
 onChange={(e) => setFormData({ ...formData, metaDescription: e.target.value })}
 />
 <p className="text-xs text-muted-foreground mt-1">Recommended: 150-160 characters</p>
 </div>
 <Input
 label="OG Image URL"
 placeholder="https://... (1200x630 recommended)"
 value={formData.ogImage}
 onChange={(e) => setFormData({ ...formData, ogImage: e.target.value })}
 />
 </>
 )}

 {activeTab === 'tracking' && (
 <>
 <Input
 label="Google Analytics ID"
 placeholder="G-XXXXXXXXXX"
 value={formData.trackingCodes.google_analytics || ''}
 onChange={(e) => updateTracking('google_analytics', e.target.value)}
 />
 <Input
 label="Facebook Pixel ID"
 placeholder="1234567890"
 value={formData.trackingCodes.facebook_pixel || ''}
 onChange={(e) => updateTracking('facebook_pixel', e.target.value)}
 />
 <div>
 <label className="text-sm font-medium">Custom Head Code</label>
 <textarea
 className="w-full mt-1 p-3 border rounded-lg font-mono text-sm min-h-[100px]"
 placeholder="<!-- Custom scripts, meta tags, etc -->"
 value={formData.trackingCodes.custom_head || ''}
 onChange={(e) => updateTracking('custom_head', e.target.value)}
 />
 </div>
 </>
 )}

 {activeTab === 'settings' && (
 <>
 <Input
 label="Page Title"
 value={formData.title}
 onChange={(e) => setFormData({ ...formData, title: e.target.value })}
 required
 />
 {canUseCustomDomains && domains.length > 0 && (
 <div>
 <label className="text-sm font-medium">Domain</label>
 <Select
 value={formData.customDomainId}
 onChange={(e) => setFormData({ ...formData, customDomainId: e.target.value })}
 className="mt-1"
 >
 <option value="">Default ({defaultDomain?.domain || 'pages.revhapi.com'})</option>
 {domains.map(d => (
 <option key={d.id} value={d.id}>{d.domain}</option>
 ))}
 </Select>
 </div>
 )}
 {formData.customDomainId ? (
 <Input
   label="URL Slug (custom domain)"
   value={formData.slug}
   onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
   hint={`${domains.find(d => d.id === formData.customDomainId)?.domain || 'customdomain.com'}/${formData.slug || 'your-slug'}`}
 />
 ) : (
 <div>
   <label className="text-sm font-medium block mb-1">URL Slug</label>
   <div className="flex items-center gap-2 px-3 py-2 border rounded-lg bg-muted/40 text-sm font-mono text-muted-foreground">
     {formData.slug || '—'}
     <span className="ml-auto text-xs bg-muted px-1.5 py-0.5 rounded">Read only</span>
   </div>
   <p className="text-xs text-muted-foreground mt-1">
     Default slugs are permanent. Add a custom domain to set your own slug.
   </p>
 </div>
 )}
 <div>
 <label className="text-sm font-medium">Status</label>
 <Select
 value={formData.status}
 onChange={(e) => setFormData({ ...formData, status: e.target.value })}
 className="mt-1"
 >
 <option value="draft">Draft</option>
 <option value="published">Published</option>
 <option value="archived">Archived</option>
 </Select>
 </div>
 </>
 )}
 </div>

 {/* Footer */}
 <div className="flex justify-between items-center pt-4 mt-4 border-t">
 <div className="text-sm text-muted-foreground">
 {selectedPage && `Last updated: ${formatDate(selectedPage.updated_at)}`}
 </div>
 <div className="flex gap-2">
 <Button variant="outline" onClick={() => { setShowEditorModal(false); setSelectedPage(null); }}>
 Cancel
 </Button>
 <Button onClick={handleSavePage} loading={saving}>
 <Save className="h-4 w-4 mr-2" />
 Save Changes
 </Button>
 </div>
 </div>
 </div>
 </Modal>
 </div>
 );
}
